import { Button, Card, Title2, Tooltip } from '@fluentui/react-components';
import {
  bundleIcon,
  ChevronDown16Filled,
  ChevronDown16Regular,
  ChevronRight16Filled,
  ChevronRight16Regular,
  Copy16Filled,
  Copy16Regular,
} from '@fluentui/react-icons';
import { useMemo, useState } from 'react';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard/useCopyToClipboard';
import {
  renderConversationHistory,
  renderTranscript,
} from '../../../utils/renderConversationHtml';

import type { GroundingPanelProps } from './GroundingPanel.interface';
import { useStyles } from './GroundingPanel.style';

const ExpandIcon = bundleIcon(
  ChevronDown16Filled,
  ChevronDown16Regular,
) as React.FC;
const CollapseIcon = bundleIcon(
  ChevronRight16Filled,
  ChevronRight16Regular,
) as React.FC;
const CopyIcon = bundleIcon(Copy16Filled, Copy16Regular) as React.FC;

export function GroundingPanel({ section, rawData }: GroundingPanelProps) {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(section.defaultExpanded);
  const { copied, copy } = useCopyToClipboard();

  const renderedHtml = useMemo(() => {
    if (section.renderer === 'conversation') {
      return renderConversationHistory(rawData);
    }
    return renderTranscript(rawData);
  }, [rawData, section.renderer]);

  return (
    <Card className={styles.root}>
      <div
        className={styles.headerRow}
        onClick={() => setExpanded((e) => !e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        <div className={styles.headerLeft}>
          {expanded ? <ExpandIcon /> : <CollapseIcon />}
          <Title2 className={styles.headerTitle}>{section.label}</Title2>
        </div>
        <Tooltip
          content={copied ? 'Copied!' : `Copy ${section.label.toLowerCase()}`}
          relationship="label"
        >
          <Button
            className={styles.copyBtn}
            appearance="subtle"
            size="small"
            icon={<CopyIcon />}
            onClick={(e) => {
              e.stopPropagation();
              copy(rawData);
            }}
            aria-label={`Copy ${section.label.toLowerCase()} to clipboard`}
          />
        </Tooltip>
      </div>

      {expanded && section.renderer === 'conversation' && (
        <div
          className={styles.conversationBox}
          dangerouslySetInnerHTML={{ __html: renderedHtml }}
        />
      )}

      {expanded &&
        section.renderer === 'transcript' &&
        (renderedHtml.length > 0 ? (
          <div
            className={styles.transcriptBoxHtml}
            dangerouslySetInnerHTML={{ __html: renderedHtml }}
          />
        ) : (
          <div className={styles.transcriptBoxPlain}>{rawData}</div>
        ))}
    </Card>
  );
}
